/**
 * Topographical mutation probes.
 *
 * Measure operational topography *before* and *after* a mutation automata
 * pass so layout/script rewrites can be falsified against parse health,
 * paired-container depth, and container counts — not only string hashes.
 *
 * Pulse stop reasons and mutation vectors come from the automata; this module
 * adds a derived topographic delta with a named parse path.
 *
 * @see docs/theory/spw/operational-topography.spw
 * @see packages/spw-seed/src/canonical/mutation-automata.ts
 */

import { parse } from '../parser'
import type { ASTNode } from '../types/ast'
import { getMaxDepth, walkAST } from '../instrumentation/audit'
import {
  runMutationAutomata,
  type MutationAutomataConfig,
  type MutationRunResult,
} from './mutation-automata'
import type { MutationVector } from './differential'
import {
  braceProjectionDelta,
  classifyMutationUsefulness,
  extractBraceProjection,
  type BraceProjection,
  type BraceProjectionDelta,
  type MutationUsefulness,
} from './brace-projection'

export type ParseHealth = 'complete_structured' | 'recovered' | 'invalid'

export interface PairedContainerCounts {
  scope: number
  frame: number
  body: number
  capsule: number
  stream: number
  nrange: number
}

/** Compact topography snapshot for one source revision. */
export interface TopographySnapshot {
  parseHealth: ParseHealth
  parserSuccess: boolean
  proseFallback: boolean
  /** Whether quoted and phrase tokens close under lexer rules. */
  lexemesClosed: boolean
  tokenCount: number
  significantTokens: number
  maxAstDepth: number | null
  maxPairedContainerDepth: number | null
  recognizedPairedContainers: PairedContainerCounts | null
  explicitCoupleOperations: number | null
  /** Declared brace semantic surface (kinds, medials, channels). */
  braceProjection: BraceProjection
  reasons: string[]
  sourceLength: number
}

/** Numeric / categorical delta across two snapshots. */
export interface TopographyDelta {
  parseHealthChanged: boolean
  parseHealthBefore: ParseHealth
  parseHealthAfter: ParseHealth
  /** True when after is worse than before (structured → recovered/invalid, etc.) */
  healthRegressed: boolean
  maxAstDepthDelta: number | null
  maxPairedContainerDepthDelta: number | null
  tokenCountDelta: number
  significantTokenDelta: number
  coupleOpsDelta: number | null
  containerDeltas: PairedContainerCounts | null
  sourceLengthDelta: number
  /** True when structure metrics moved (depth, containers, brace projection) */
  structureMoved: boolean
  /** True when only layout-ish surface metrics moved and brace projection is stable */
  layoutOnlyCandidate: boolean
  /** Brace projection equality + kind/channel/placement findings */
  brace: BraceProjectionDelta
  /** Agent-oriented next action from this delta (set when mutation context available) */
  usefulness?: MutationUsefulness
}

export interface TopographyMutationProbe {
  /** Mutation automata result (plan or apply per config) */
  mutation: MutationRunResult
  before: TopographySnapshot
  /** Snapshot of mutated source when applied; equals before when dry-run with no apply */
  after: TopographySnapshot
  /** Topography of the *planned* output even when dry-run (virtual after) */
  plannedAfter: TopographySnapshot
  delta: TopographyDelta
  /** Planned vs input delta (always uses virtual post-mutation source) */
  plannedDelta: TopographyDelta
  vector: MutationVector
  /** Human-oriented summary lines for CLI */
  findings: string[]
}

function emptyContainers(): PairedContainerCounts {
  return { scope: 0, frame: 0, body: 0, capsule: 0, stream: 0, nrange: 0 }
}

function pairedKind(node: ASTNode): keyof PairedContainerCounts | null {
  switch (node.type) {
    case 'Scope':
      return 'scope'
    case 'Frame':
      return 'frame'
    case 'Body':
      return 'body'
    case 'Capsule':
      return 'capsule'
    case 'Stream':
      return 'stream'
    case 'NRange':
      return 'nrange'
    default:
      return null
  }
}

interface LexemeClosureToken {
  type: string
  kind?: string
  value: string
}

/**
 * Report whether every quoted and phrase token has a closing delimiter. A
 * delimiter preceded by an even run of backslashes is closed; an odd run
 * escapes it. Tooling should share this rule rather than infer closure from
 * the final two characters.
 *
 * Spw has no block comments, so there is no third delimiter pair to close.
 */
function lexemesAreClosed(tokens: readonly LexemeClosureToken[]): boolean {
  return tokens.every(token => {
    if (token.type === 'PHRASE') {
      return token.value.startsWith('`') && endsWithUnescapedDelimiter(token.value, '`')
    }
    if (token.type === 'STRING') {
      const q = token.value[0]
      return (
        (q === '"' || q === "'") &&
        token.value.startsWith(q) &&
        endsWithUnescapedDelimiter(token.value, q)
      )
    }
    return true
  })
}

function endsWithUnescapedDelimiter(value: string, delimiter: string): boolean {
  if (value.length < 2 || value.at(-1) !== delimiter) return false

  let precedingBackslashes = 0
  for (let index = value.length - 2; index >= 0 && value[index] === '\\'; index -= 1) {
    precedingBackslashes += 1
  }
  return precedingBackslashes % 2 === 0
}

/**
 * Snapshot operational topography for a source string.
 *
 * Fields read from parser output are observed syntax/structure evidence for
 * this source revision; classifications computed here are derived evidence.
 */
export function snapshotTopography(source: string): TopographySnapshot {
  const output = parse(source)
  const { tokens, ast, errors } = output
  const significantTokens = tokens.filter(
    t => t.type !== 'WHITESPACE' && t.type !== 'COMMENT' && t.type !== 'EOF',
  ).length
  const proseFallback = ast?.expression?.type === 'Prose'
  const nonRecoverableError = errors.some(
    error => (error.data as { recoverable?: boolean } | undefined)?.recoverable === false,
  )
  const lexemesClosed = lexemesAreClosed(tokens)
  const reasons: string[] = []

  if (!output.success) reasons.push('parser_failure')
  if (!ast) reasons.push('missing_ast')
  if (nonRecoverableError) reasons.push('non_recoverable_error')
  if (!lexemesClosed) reasons.push('unterminated_lexeme')

  const invalid = reasons.length > 0
  if (!invalid && errors.length > 0) reasons.push('recoverable_errors')
  if (!invalid && proseFallback) reasons.push('prose_fallback')

  const parseHealth: ParseHealth = invalid
    ? 'invalid'
    : reasons.length > 0
      ? 'recovered'
      : 'complete_structured'

  const recognizedPairedContainers = ast ? emptyContainers() : null
  let explicitCoupleOperations: number | null = ast ? 0 : null
  let maxPairedContainerDepth: number | null = ast ? 0 : null
  const braceProjection = extractBraceProjection(ast ?? null)

  if (ast && recognizedPairedContainers) {
    walkAST(ast, (node, path) => {
      const kind = pairedKind(node)
      if (kind) recognizedPairedContainers[kind] += 1
      if (
        node.type === 'Operation' &&
        (node as { operator?: { value?: string } }).operator?.value === '<>'
      ) {
        explicitCoupleOperations = (explicitCoupleOperations ?? 0) + 1
      }
      const ancestorPaired = path.reduce(
        (depth, ancestor) => depth + (pairedKind(ancestor) ? 1 : 0),
        0,
      )
      const nodeDepth = ancestorPaired + (kind ? 1 : 0)
      if (maxPairedContainerDepth === null || nodeDepth > maxPairedContainerDepth) {
        maxPairedContainerDepth = nodeDepth
      }
    })
  }

  return {
    parseHealth,
    parserSuccess: output.success,
    proseFallback: Boolean(proseFallback),
    lexemesClosed,
    braceProjection,
    tokenCount: tokens.filter(t => t.type !== 'EOF').length,
    significantTokens,
    maxAstDepth: ast ? getMaxDepth(ast) : null,
    maxPairedContainerDepth,
    recognizedPairedContainers,
    explicitCoupleOperations,
    reasons,
    sourceLength: source.length,
  }
}

function healthRank(h: ParseHealth): number {
  if (h === 'complete_structured') return 2
  if (h === 'recovered') return 1
  return 0
}

/**
 * Stratified delta between two topography snapshots.
 */
export function topographyDelta(
  before: TopographySnapshot,
  after: TopographySnapshot,
): TopographyDelta {
  const maxAstDepthDelta =
    before.maxAstDepth !== null && after.maxAstDepth !== null
      ? after.maxAstDepth - before.maxAstDepth
      : null
  const maxPairedContainerDepthDelta =
    before.maxPairedContainerDepth !== null && after.maxPairedContainerDepth !== null
      ? after.maxPairedContainerDepth - before.maxPairedContainerDepth
      : null

  let containerDeltas: PairedContainerCounts | null = null
  let containerMoved = false
  if (before.recognizedPairedContainers && after.recognizedPairedContainers) {
    containerDeltas = emptyContainers()
    for (const key of Object.keys(containerDeltas) as (keyof PairedContainerCounts)[]) {
      containerDeltas[key] =
        after.recognizedPairedContainers[key] - before.recognizedPairedContainers[key]
      if (containerDeltas[key] !== 0) containerMoved = true
    }
  }

  const coupleOpsDelta =
    before.explicitCoupleOperations !== null && after.explicitCoupleOperations !== null
      ? after.explicitCoupleOperations - before.explicitCoupleOperations
      : null

  const brace = braceProjectionDelta(before.braceProjection, after.braceProjection)

  const structureMoved =
    containerMoved ||
    (maxAstDepthDelta !== null && maxAstDepthDelta !== 0) ||
    (maxPairedContainerDepthDelta !== null && maxPairedContainerDepthDelta !== 0) ||
    (coupleOpsDelta !== null && coupleOpsDelta !== 0) ||
    !brace.equal

  const surfaceMoved =
    before.tokenCount !== after.tokenCount ||
    before.significantTokens !== after.significantTokens ||
    before.sourceLength !== after.sourceLength

  const parseHealthChanged = before.parseHealth !== after.parseHealth
  const healthRegressed = healthRank(after.parseHealth) < healthRank(before.parseHealth)

  return {
    parseHealthChanged,
    parseHealthBefore: before.parseHealth,
    parseHealthAfter: after.parseHealth,
    healthRegressed,
    maxAstDepthDelta,
    maxPairedContainerDepthDelta,
    tokenCountDelta: after.tokenCount - before.tokenCount,
    significantTokenDelta: after.significantTokens - before.significantTokens,
    coupleOpsDelta,
    containerDeltas,
    sourceLengthDelta: after.sourceLength - before.sourceLength,
    structureMoved,
    layoutOnlyCandidate:
      !structureMoved &&
      brace.equal &&
      !parseHealthChanged &&
      !healthRegressed &&
      surfaceMoved &&
      before.parseHealth === after.parseHealth,
    brace,
  }
}

function findingsFrom(
  mutation: MutationRunResult,
  delta: TopographyDelta,
  plannedDelta: TopographyDelta,
): string[] {
  const lines: string[] = []
  lines.push(
    `stop=${mutation.stopReason} profile=${mutation.profile} changed=${mutation.changed} dryRun=${mutation.dryRun}`,
  )
  lines.push(
    `vector edits=${mutation.vector.edit_count} layout=${mutation.vector.layout_delta} script=${mutation.vector.script_delta} bytes=${mutation.vector.bytes_delta}`,
  )

  const d = plannedDelta
  lines.push(
    `topo health ${d.parseHealthBefore} → ${d.parseHealthAfter}` +
      (d.healthRegressed ? ' (REGRESSED)' : d.parseHealthChanged ? ' (changed)' : ' (stable)'),
  )
  if (d.brace.equal) {
    lines.push('brace projection: stable (kinds/placement/channels)')
  } else {
    lines.push(`brace projection: DRIFT severity=${d.brace.severity}`)
    for (const f of d.brace.findings.slice(0, 4)) {
      lines.push(`  brace · ${f}`)
    }
  }
  if (d.layoutOnlyCandidate) {
    lines.push('topo layout-only candidate: brace+structure stable, surface metrics moved')
  }
  if (d.structureMoved) {
    lines.push(
      `topo structure moved: astDepthΔ=${d.maxAstDepthDelta ?? 'n/a'} pairedDepthΔ=${d.maxPairedContainerDepthDelta ?? 'n/a'} coupleΔ=${d.coupleOpsDelta ?? 'n/a'}`,
    )
  }
  if (d.containerDeltas) {
    const parts = Object.entries(d.containerDeltas)
      .filter(([, v]) => v !== 0)
      .map(([k, v]) => `${k}${v > 0 ? '+' : ''}${v}`)
    if (parts.length) lines.push(`topo containers: ${parts.join(' ')}`)
  }
  const use = classifyMutationUsefulness({
    changed: mutation.changed,
    healthRegressed: d.healthRegressed,
    parseHealthy:
      d.parseHealthBefore === 'complete_structured' &&
      d.parseHealthAfter === 'complete_structured',
    braceEqual: d.brace.equal,
    structureMoved: d.structureMoved,
    layoutOnlyCandidate: d.layoutOnlyCandidate,
    layoutVectorPositive: mutation.vector.layout_delta > 0,
    nonLayoutVectorAxes:
      mutation.vector.token_delta !== 0 ||
      mutation.vector.structure_delta !== 0 ||
      mutation.vector.label_delta !== 0 ||
      mutation.vector.reference_delta !== 0 ||
      mutation.vector.script_delta !== 0,
  })
  lines.push(`use: ${use.class} — ${use.advice}`)
  if (!mutation.changed && mutation.vector.edit_count === 0) {
    lines.push('fixed point: no planned edits')
  }
  if (mutation.requiresWriteAuthority) {
    lines.push('effect.l2.workspace write authority required to persist')
  }
  // Keep delta (applied) vs planned when dry-run differs
  if (mutation.dryRun && plannedDelta.sourceLengthDelta !== delta.sourceLengthDelta) {
    lines.push('dry-run: applied snapshot unchanged; planned delta above is virtual')
  }
  return lines
}

/**
 * Run mutation automata and probe topography before / after (and planned).
 *
 * Default config is dry-run layout_canonical so CLI pulses are effect.l0.measure-safe.
 */
export function probeMutationTopography(
  source: string,
  config: MutationAutomataConfig = { profile: 'layout_canonical', dryRun: true },
): TopographyMutationProbe {
  const before = snapshotTopography(source)
  const mutation = runMutationAutomata(source, {
    dryRun: true,
    ...config,
  })

  const plannedAfter = snapshotTopography(mutation.plannedSource)
  const after = snapshotTopography(mutation.source)
  const delta = topographyDelta(before, after)
  const plannedDelta = topographyDelta(before, plannedAfter)
  const usefulness = classifyMutationUsefulness({
    changed: mutation.changed,
    healthRegressed: plannedDelta.healthRegressed,
    parseHealthy:
      plannedDelta.parseHealthBefore === 'complete_structured' &&
      plannedDelta.parseHealthAfter === 'complete_structured',
    braceEqual: plannedDelta.brace.equal,
    structureMoved: plannedDelta.structureMoved,
    layoutOnlyCandidate: plannedDelta.layoutOnlyCandidate,
    layoutVectorPositive: mutation.vector.layout_delta > 0,
    nonLayoutVectorAxes:
      mutation.vector.token_delta !== 0 ||
      mutation.vector.structure_delta !== 0 ||
      mutation.vector.label_delta !== 0 ||
      mutation.vector.reference_delta !== 0 ||
      mutation.vector.script_delta !== 0,
  })
  plannedDelta.usefulness = usefulness
  delta.usefulness = usefulness

  return {
    mutation,
    before,
    after,
    plannedAfter,
    delta,
    plannedDelta,
    vector: mutation.vector,
    findings: findingsFrom(mutation, delta, plannedDelta),
  }
}
